// Workspace-level urgency data bag (D1).
//
// Mounted by `LeadWorkspaceShell` so the chrome surfaces (`SidebarModeList`,
// `MobileBottomTabBar`) — and any other consumer that wants to peek at the
// shared signal (e.g. the `ActivityBannerAdAlertsSection`, which reads
// `adsActivity` from here so the bell pulls from a single fetch) — can read
// the inputs without prop-drilling through the bucket switcher.
//
// The provider holds raw data only; it does NOT decide urgency itself. That
// decision lives in `modeUrgencyResolvers.ts`, invoked through
// `useModeUrgency(mode)`. Keeping the context dumb means a follow-on
// workstream can add new resolver inputs (e.g. KB gap counts) by widening
// this bag without changing the resolver contract.

import { createContext, useContext, useMemo, type ReactNode } from "react";
import type { AdsActivityResponse } from "@/lib/adsAssistantClientEngagement";
import type { Lead } from "@/types/lead";

export type WorkspaceUrgencyContextValue = {
  leads: Lead[];
  adsActivity: AdsActivityResponse | null;
  /**
   * Trigger a re-fetch of the shell's `useAdsHeaderActivity` poll. Exposed so
   * the Activity-mode panel's refresh button can drive the same single fetch
   * as the bell — no per-panel polling clones.
   */
  refreshAdsActivity: () => Promise<void> | void;
  /**
   * The lead the user is actively viewing/editing (or `null` when no lead is
   * open). Surfaced so shell-level chrome — like the activity dock's
   * active-lead checklist section — can react without prop-drilling through
   * the bucket switcher. See
   * docs/planning/org_activity_floating_dock.md §11.
   */
  currentLead: Lead | null;
};

const WorkspaceUrgencyContext = createContext<WorkspaceUrgencyContextValue | null>(null);

export type WorkspaceUrgencyProviderProps = {
  leads: Lead[];
  adsActivity: AdsActivityResponse | null;
  refreshAdsActivity: () => Promise<void> | void;
  currentLead: Lead | null;
  children: ReactNode;
};

export function WorkspaceUrgencyProvider({
  leads,
  adsActivity,
  refreshAdsActivity,
  currentLead,
  children,
}: WorkspaceUrgencyProviderProps) {
  const value = useMemo<WorkspaceUrgencyContextValue>(
    () => ({ leads, adsActivity, refreshAdsActivity, currentLead }),
    [leads, adsActivity, refreshAdsActivity, currentLead],
  );
  return (
    <WorkspaceUrgencyContext.Provider value={value}>{children}</WorkspaceUrgencyContext.Provider>
  );
}

/**
 * Returns the workspace urgency data bag, or `null` if the consumer is mounted
 * outside the provider. Chrome surfaces always live inside the provider, but
 * leaving the nullable return keeps the contract safe for future consumers
 * (e.g. dev-only panels) that might mount above the shell.
 */
export function useWorkspaceUrgencyContext(): WorkspaceUrgencyContextValue | null {
  return useContext(WorkspaceUrgencyContext);
}
